import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCanWithoutBuilding } from "@/lib/policy";
import { EMPTY_FAQ, FaqForm, type FaqValues } from "./-components/faq-form";
import { FaqInquiryList, type FaqInquiry } from "./-components/faq-inquiry-list";
import { FaqList, type Faq } from "./-components/faq-list";
import { FaqDocumentsCard } from "./-components/faq-documents-card";

export const Route = createFileRoute("/_authenticated/_workforce/hiring/faqs")({
  component: HiringFaqs,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The careers assistant: what it is allowed to tell candidates, and what it was asked but could
 * not answer. */
function HiringFaqs() {
  const { facilityId } = useFacility();
  const building = facilityId === "all" ? null : facilityId;
  const canManage = useCanWithoutBuilding("hiring:manage");
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<FaqValues | null>(null);
  const [editing, setEditing] = useState<Faq | null>(null);
  const [answering, setAnswering] = useState<FaqInquiry | null>(null);
  const [deleting, setDeleting] = useState<Faq | null>(null);

  const faqs = useQuery(
    orpc.workforce.hiringCatalog.listFaqs.queryOptions({ input: { facilityId } }),
  );
  const inquiries = useQuery(
    orpc.workforce.hiringCatalog.listFaqInquiries.queryOptions({
      input: { facilityId, status: "new" },
    }),
  );
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringCatalog.key() });

  const closeForm = () => {
    setDraft(null);
    setEditing(null);
    setAnswering(null);
  };

  const resolve = useMutation(
    orpc.workforce.hiringCatalog.resolveFaqInquiry.mutationOptions({
      onSuccess: invalidate,
      onError: fail,
    }),
  );

  const save = useMutation(
    orpc.workforce.hiringCatalog.saveFaq.mutationOptions({
      onSuccess: async () => {
        // The question is only cleared once its answer is safely stored.
        if (answering) {
          resolve.mutate({
            inquiryId: answering.id,
            staffNote: "Added to the careers answers.",
          });
        }
        toast.success(editing ? "Answer updated" : "Answer added");
        closeForm();
        await invalidate();
      },
      onError: fail,
    }),
  );

  const remove = useMutation(
    orpc.workforce.hiringCatalog.deleteFaq.mutationOptions({
      onSuccess: async () => {
        toast.success("Answer removed");
        setDeleting(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const submit = (values: FaqValues) => {
    const target = editing?.facilityId ?? answering?.facilityId ?? building;
    if (!target) return;
    const category = values.category?.trim();
    save.mutate({
      ...(editing ? { faqId: editing.id } : {}),
      facilityId: target,
      question: values.question,
      answer: values.answer,
      category: category ? category : null,
    });
  };

  return (
    <>
      <PageHeader
        title="Careers assistant"
        meta="The answers candidates are given, and the questions still waiting for one."
        actions={
          canManage && building ? (
            <Button
              onClick={() => {
                setEditing(null);
                setAnswering(null);
                setDraft(EMPTY_FAQ);
              }}
            >
              New answer
            </Button>
          ) : null
        }
      />

      {canManage && !building ? (
        <p className="text-muted-foreground text-sm">
          Each building's careers page has its own answers and documents. Choose one in the building
          switcher at the top to add them.
        </p>
      ) : null}

      <div className="space-y-4">
        {faqs.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : faqs.isError ? (
          <ReadFailed
            message="Could not load the assistant's answers."
            error={faqs.error}
            retrying={faqs.isFetching}
            onRetry={() => void faqs.refetch()}
          />
        ) : (
          <FaqList
            faqs={faqs.data ?? []}
            canManage={canManage}
            onEdit={(faq) => {
              setEditing(faq);
              setAnswering(null);
              setDraft({
                question: faq.question,
                answer: faq.answer,
                category: faq.category ?? "",
              });
            }}
            onDelete={setDeleting}
          />
        )}

        <FaqDocumentsCard facilityId={building} canManage={canManage} />

        {inquiries.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : inquiries.isError ? (
          <ReadFailed
            message="Could not load the questions still waiting for an answer."
            error={inquiries.error}
            retrying={inquiries.isFetching}
            onRetry={() => void inquiries.refetch()}
          />
        ) : (
          <FaqInquiryList
            inquiries={inquiries.data ?? []}
            canManage={canManage}
            onAnswer={(inquiry) => {
              setEditing(null);
              setAnswering(inquiry);
              setDraft({ ...EMPTY_FAQ, question: inquiry.question });
            }}
            onDismiss={(inquiry) =>
              resolve.mutate(
                { inquiryId: inquiry.id, staffNote: "Dismissed without an answer." },
                { onSuccess: () => toast.success("Question cleared") },
              )
            }
          />
        )}
      </div>

      <Dialog open={draft !== null} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit answer" : "New answer"}</DialogTitle>
            <DialogDescription>
              {answering
                ? "A candidate asked this and the assistant could not answer. Write the answer and it will handle it from now on."
                : "The assistant answers only from these entries. It will never invent a pay rate, a benefit, or an application outcome."}
            </DialogDescription>
          </DialogHeader>
          {draft ? (
            <FaqForm
              defaultValues={draft}
              pending={save.isPending}
              submitLabel={editing ? "Save changes" : "Add answer"}
              onCancel={closeForm}
              onSubmit={submit}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this answer?</AlertDialogTitle>
            <AlertDialogDescription>
              Candidates stop seeing it, and the assistant hands this question to your team instead.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleting && remove.mutate({ faqId: deleting.id })}
              disabled={remove.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
